import * as THREE from 'three';

import CruciblePiece from './cruciblePieces/cruciblePiece';
import CrucibleColors from './crucibleColors';
import CrucibleMetadata from './crucibleMetadata';
import CrucibleData from '../crucibleData';
import CrucibleLoader from './crucibleLoader';
import { CentralPiece } from './cruciblePieces/centralPiece';
import { Runes } from './cruciblePieces/runes';
import { Shell } from './cruciblePieces/shell';
import { Gyroscope } from './cruciblePieces/gyroscope';
import { Frame } from './cruciblePieces/frame';
import { Clock } from './cruciblePieces/clock';
import { Gems } from './cruciblePieces/gems';
import { Multiplier } from './cruciblePieces/multiplier';
import { Cogwheels } from './cruciblePieces/cogwheels';
import { PlanetaryClock } from './cruciblePieces/planetaryClock';
import { RewardProgramsDisc } from './cruciblePieces/rewardProgramsDisc';
import { MagicCircle } from './cruciblePieces/magicCircle';
import { CrucibleIdText } from './cruciblePieces/crucibleIdText';
import getCrucibleExtras from './cruciblePieces/extras/getCrucibleExtras';
import config from './config.json';
import CrucibleScene from './crucibleScene';
import Background from './background';

export default class CrucibleGroup {
  public group: THREE.Group;
  public background: Background;
  public idTextVisible: boolean;
  public loader: CrucibleLoader;
  public colors: CrucibleColors;
  public isUpdated: boolean;
  public isLoaded: boolean;

  protected _renderer: THREE.WebGLRenderer;

  protected _runes: Runes;
  protected _centralPiece: CentralPiece;
  protected _shell: Shell;
  protected _gyroscope: Gyroscope;
  protected _frame: Frame;
  protected _clock: Clock;
  protected _multiplier: Multiplier;
  protected _cogwheels: Cogwheels;
  protected _planetaryClock: PlanetaryClock;
  protected _rewardProgramsDisc: RewardProgramsDisc;
  protected _magicCircle: MagicCircle;
  protected _gems: Gems;
  protected _crucibleIdText: CrucibleIdText | null;
  protected _color: THREE.Color;
  protected _extras: CruciblePiece[];

  constructor(
    renderer: THREE.WebGLRenderer,
    idTextVisible: boolean,
    scene: CrucibleScene
  ) {
    this.group = new THREE.Group();
    this.group.name = 'Crucible';

    this.loader = new CrucibleLoader(renderer, scene.options.textureQuality);
    this.idTextVisible = idTextVisible;
    this.isUpdated = false;
    this.isLoaded = false;

    this.colors = new CrucibleColors();
    this.background = new Background(scene);

    this._renderer = renderer;

    this._rewardProgramsDisc = new RewardProgramsDisc();
    this._planetaryClock = new PlanetaryClock();
    this._centralPiece = new CentralPiece();
    this._multiplier = new Multiplier();
    this._cogwheels = new Cogwheels();
    this._gyroscope = new Gyroscope();
    this._runes = new Runes();
    this._shell = new Shell();
    this._frame = new Frame();
    this._clock = new Clock();
    this._gems = new Gems();
    this._magicCircle = new MagicCircle();
    this._color = new THREE.Color('#ffffff');

    this._crucibleIdText = null;

    this._extras = [];
  }

  async load(
    metadata: CrucibleMetadata,
    idTextVisible: boolean,
    textureQuality: string
  ) {
    // @ts-ignore
    this.loader.quality = config.sceneConfig.quality[textureQuality];

    metadata.prng.deletePrngChannel('colors');
    this.colors.setup(metadata.prng);
    this._color = new THREE.Color(metadata.color);
    this._extras = getCrucibleExtras(
      metadata,
      this._color,
      this.loader,
      this._renderer
    );

    this.isUpdated = false;
    this.isLoaded = false;
    this.idTextVisible = idTextVisible;

    await this.background.load(this.loader, metadata.prng, this.colors);
    this.group.add(this.background.group);

    const modelPromises: Promise<void>[] = [];

    // Load Shell first
    modelPromises.push(
      this.loader
        .loadModel(config.shell.baseModelUrl)
        .then(async (gltf: any) => {
          await this._shell.setup(gltf);
          await this._shell.loadAllComponents(
            this.loader,
            metadata.shellInnerRing.modelUrl,
            metadata.shellOuterRing.modelUrl,
            metadata.shellOrnamentRing.modelUrl,
            metadata.shellGlass.modelUrl,
            metadata.shellCutShape.modelUrl
          );
        })
    );

    modelPromises.push(
      this.loader
        .loadModel(metadata.runes.modelUrl)
        .then((gltf: any) => this._runes.setup(gltf))
    );

    modelPromises.push(
      this.loader
        .loadModel(metadata.centralPiece.modelUrl)
        .then((gltf: any) => this._centralPiece.setup(gltf))
    );

    modelPromises.push(
      this.loader
        .loadModel(metadata.gyroscope.modelUrl)
        .then((gltf: any) => this._gyroscope.setup(gltf))
    );

    modelPromises.push(
      this.loader
        .loadModel(metadata.multiplier.modelUrl)
        .then((gltf: any) => this._multiplier.setup(gltf))
    );

    modelPromises.push(
      this.loader
        .loadModel(metadata.rewardProgram.modelUrl)
        .then((gltf: any) => this._rewardProgramsDisc.setup(gltf))
    );

    modelPromises.push(
      this.loader
        .loadModel(metadata.cogwheels.modelUrl)
        .then((gltf: any) => this._cogwheels.setup(gltf))
    );

    modelPromises.push(
      this.loader.loadModel(metadata.gems.modelUrl).then((gltf: any) => {
        this._gems.setColor(this._color);
        this._gems.setup(gltf);
      })
    );

    modelPromises.push(
      this.loader
        .loadModel(metadata.frame.modelUrl)
        .then((gltf: any) => this._frame.setup(gltf))
    );

    modelPromises.push(
      this.loader.loadModel(metadata.clock.modelUrl).then((gltf: any) => {
        this._clock.setup(gltf);
        this._clock.setupMintDate(metadata.mintTimestamp);
      })
    );

    modelPromises.push(
      this.loader
        .loadModel(metadata.planetaryClock.modelUrl)
        .then((gltf: any) => this._planetaryClock.setup(gltf))
    );

    modelPromises.push(
      this.loader
        .loadModel(metadata.magicCircle.modelUrl)
        .then((gltf: any) => this._magicCircle.setup(gltf))
    );

    await Promise.all(modelPromises);

    if (this.idTextVisible) {
      this._crucibleIdText = new CrucibleIdText(
        metadata.crucibleId,
        this._shell,
        this._color
      );
    }
  }

  async renderCruciblePieces(screenshotMode: boolean) {
    const cruciblePiecesGroup: CruciblePiece[] = [
      this._runes,
      this._centralPiece,
      this._gyroscope,
      this._frame,
      this._clock,
      this._multiplier,
      this._rewardProgramsDisc,
      this._planetaryClock,
      this._cogwheels,
      this._gems,
      this._magicCircle
    ];

    this._magicCircle.setColor(this._color);

    this.group.add(this._shell.group);

    cruciblePiecesGroup.forEach((piece) => {
      this._delayedAddToGroup(
        1000 + 30 * cruciblePiecesGroup.indexOf(piece),
        piece,
        screenshotMode
      );
    });

    if (this.idTextVisible && this._crucibleIdText !== null) {
      await this._crucibleIdText.loadAndAddToScene();
    }

    this._extras.forEach((extraPiece) => this.group.add(extraPiece.group));

    this.isLoaded = true;
  }

  _delayedAddToGroup(
    time: number,
    piece: CruciblePiece,
    screenshotMode: boolean
  ) {
    if (!screenshotMode) {
      setTimeout(() => {
        this.group.add(piece.group);
      }, time);
    } else {
      this.group.add(piece.group);
    }
  }

  openShellInstantly() {
    this._shell.playKeyFrameAnimations(100);
    this._magicCircle.playKeyFrameAnimations(100);
  }

  playKeyframeAnimations() {
    this._shell.playKeyFrameAnimations();
    this._magicCircle.playKeyFrameAnimations();
  }

  playKeyframeAnimationsReversed() {
    this._shell.playKeyFrameAnimationsReversed();
    this._magicCircle.playKeyFrameAnimationsReversed();
  }

  playOnClick(raycaster: THREE.Raycaster) {
    this._clock.playOnClick(raycaster);
    this._planetaryClock.playOnClick(raycaster);
    this._gems.playOnClick(raycaster);
    this._runes.playOnClick(raycaster);
  }

  update(
    crucibleData: CrucibleData,
    metadata: CrucibleMetadata,
    scene: THREE.Scene
  ) {
    if (this.isUpdated) return;

    const crucibleLevel = this._getCrucibleLevel(crucibleData);
    const crucibleTier = this._getCrucibleTier(crucibleLevel);
    console.log('Crucible Level', crucibleLevel, 'Tier', crucibleTier);

    this._shell.update(crucibleData.mintNumber, this.loader);

    this._runes.update(crucibleData, this.loader, this._color);

    this._gems.update(
      crucibleLevel,
      crucibleTier,
      metadata.color,
      this.loader,
      metadata.prng,
      scene
    );

    this._centralPiece.update(
      crucibleLevel,
      crucibleTier,
      this._color,
      scene,
      this.loader,
      this.colors,
      metadata.prng
    );

    this.background.update(
      this.loader,
      this.colors,
      metadata.prng,
      crucibleData.mintNumber,
      crucibleData.christmasFlag
    );

    this._rewardProgramsDisc.update(
      this._color,
      this.loader,
      crucibleData,
      metadata.coinStyle
    );

    this._multiplier.update(crucibleData.totalSubscribedValue, this._color);

    this._clock.update(
      crucibleData.mintNumber,
      crucibleData.mintTimestamp,
      this._color
    );

    this.isUpdated = true;
  }

  animate(timeElapsed: number, screenshotMode: boolean) {
    if (!screenshotMode) {
      this.background.animate();

      if (!this.isLoaded) return;

      this._runes.animate(timeElapsed);
      this._centralPiece.animate(timeElapsed);
      this._shell.animate(timeElapsed);
      this._gyroscope.animate(timeElapsed);
      this._frame.animate(timeElapsed);
      this._clock.animate(timeElapsed);
      this._multiplier.animate(timeElapsed);
      this._rewardProgramsDisc.animate(timeElapsed);
      this._planetaryClock.animate(timeElapsed);
      this._cogwheels.animate(timeElapsed);
      this._gems.animate(timeElapsed);
      this._magicCircle.animate(timeElapsed);

      if (this.idTextVisible && this._crucibleIdText !== null)
        this._crucibleIdText.animate(timeElapsed);

      this._extras.forEach((extraPiece) => extraPiece.animate(timeElapsed));
    } else {
      this._shell.animate(timeElapsed, screenshotMode);
      this._magicCircle.animate(timeElapsed, screenshotMode);
    }
  }

  _getCrucibleLevel(data: CrucibleData): number {
    const aludelSubs = data.aludelSubscriptions;

    const amountWeight = config.crucibleLevelConfig.balanceWeight;
    const durationWeight = config.crucibleLevelConfig.durationWeight;

    let level = 0;

    let totalDurationSubbed = 0;
    let totalWeightedAmountSubbed = 0;

    aludelSubs.forEach((sub) => {
      const durationInDays = sub.duration / 24 / 60 / 60; // the duration is currently in seconds
      totalWeightedAmountSubbed += sub.amount * durationInDays;
      totalDurationSubbed += durationInDays;
    });

    level =
      (totalWeightedAmountSubbed / totalDurationSubbed) * amountWeight +
      totalDurationSubbed * durationWeight;

    return level;
  }

  _getCrucibleTier(level: number): number {
    const tierMinLevels = config.crucibleLevelConfig.tierMinLevels;
    let crucibleTier = 0;
    tierMinLevels.forEach((minLevel, tier) => {
      if (level > minLevel) crucibleTier = tier;
    });

    return crucibleTier;
  }
}
